import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { CheckCircle2, KeyRound, ArrowLeft, AlertTriangle } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';
import PasswordStrengthMeter from '@/components/PasswordStrengthMeter';
import { validatePassword } from '@/lib/passwordRules';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { checkAuth } = useAuth();
  const token = params.get('token') || '';
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // `fatalError` shows a full-panel friendly error state (with a
  // "Request a new link" button) for token-level failures where the
  // form itself is useless: expired link, already-consumed link,
  // unrecognised token. `.email` (if present) is carried forward to
  // the ForgotPassword page so the user doesn't retype it.
  const [fatalError, setFatalError] = useState(null);
  const [done, setDone] = useState(false);

  // Round 95p·6 — On mount, ask the backend whether the token is
  // valid BEFORE the user types anything. If it's expired/used the
  // fatal screen fires immediately (and carries the token's email
  // through to /forgot-password so they don't retype it).
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    axios
      .get(`${API}/auth/reset-password/check`, { params: { token } })
      .then(r => {
        if (cancelled) return;
        const { status, email } = r.data || {};
        if (status === 'expired') {
          setFatalError({ kind: 'expired', email,
            title: 'This reset link has expired',
            body: 'Reset links only stay valid for 30 minutes. Request a fresh one to continue.' });
        } else if (status === 'used') {
          setFatalError({ kind: 'used', email,
            title: 'This reset link was already used',
            body: 'Each link only works once. Request a fresh one if you need to change your password again.' });
        } else if (status === 'invalid') {
          setFatalError({ kind: 'invalid', email,
            title: 'This reset link isn\'t valid',
            body: 'The link may have been mistyped or the token was rotated. Request a fresh one below.' });
        }
      })
      .catch(() => { /* non-blocking — form stays usable */ });
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (!validatePassword(pw1).valid) {
      setError('Password must be 8+ chars with at least 1 uppercase, 1 lowercase, and 1 number.');
      return;
    }
    if (pw1 !== pw2) {
      setError('The two passwords don\'t match.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/reset-password`,
        { token, new_password: pw1 },
        { withCredentials: true },
      );
      setDone(true);
      // Refresh auth context so the app sees the user as logged in.
      try { if (checkAuth) await checkAuth(); } catch { /* non-blocking */ }
      toast.success('Password updated', { description: 'You\'re signed in.' });
      setTimeout(() => navigate('/'), 1600);
    } catch (err) {
      const detail = err?.response?.data?.detail || '';
      const email = err?.response?.data?.email;
      const lower = String(detail).toLowerCase();
      if (lower.includes('expired')) {
        setFatalError({ kind: 'expired', email, title: 'This reset link has expired',
          body: 'Reset links only stay valid for 30 minutes. Request a fresh one to continue.' });
      } else if (lower.includes('already been used')) {
        setFatalError({ kind: 'used', email, title: 'This reset link was already used',
          body: 'Each link only works once. Request a fresh one if you need to change your password again.' });
      } else if (lower.includes('invalid')) {
        setFatalError({ kind: 'invalid', email, title: 'This reset link isn\'t valid',
          body: 'The link may have been mistyped or the token was rotated. Request a fresh one below.' });
      } else {
        setError(detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!token || fatalError) {
    const errState = fatalError || {
      kind: 'invalid',
      title: 'Invalid reset link',
      body: 'This URL is missing the reset token. Ask for a fresh link below.',
    };
    const requestNewHref = errState.email
      ? `/forgot-password?email=${encodeURIComponent(errState.email)}`
      : '/forgot-password';
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md bg-card border rounded-xl p-8 shadow-lg text-center"
             data-testid={`reset-password-${errState.kind === 'invalid' && !fatalError ? 'invalid' : `fatal-${errState.kind}`}`}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-100 dark:bg-rose-500/15 ring-1 ring-rose-500/30 dark:ring-rose-400/40 shadow-lg dark:shadow-rose-500/20 mb-4">
            <AlertTriangle className="w-8 h-8 text-rose-600 dark:text-rose-300" />
          </div>
          <h1 className="text-xl font-bold mb-2">{errState.title}</h1>
          <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
            {errState.body}
          </p>
          <Button
            onClick={() => navigate(requestNewHref)}
            className="w-full h-10 font-semibold"
            data-testid="reset-password-request-new"
          >
            Request a new link
          </Button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="mt-3 text-xs text-muted-foreground hover:text-foreground"
            data-testid="reset-password-fatal-home"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md bg-card border rounded-xl p-8 shadow-lg">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Dashboard
        </button>

        {done ? (
          <div className="text-center space-y-4" data-testid="reset-password-done">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-500/15 ring-1 ring-emerald-500/30 dark:ring-emerald-400/40 shadow-lg dark:shadow-emerald-500/20">
              <CheckCircle2 className="w-8 h-8 text-emerald-600 dark:text-emerald-300" />
            </div>
            <h1 className="text-xl font-bold">Password updated</h1>
            <p className="text-sm text-muted-foreground">
              You&apos;re signed in. Redirecting you to the Dashboard...
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-primary/10">
                <KeyRound className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-bold leading-none">Set a new password</h1>
                <p className="text-xs text-muted-foreground mt-1">Must be 8+ chars with an uppercase, lowercase, and number.</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3" data-testid="reset-password-form">
              <PasswordInput
                data-testid="reset-password-new"
                testIdBase="reset-password-new"
                placeholder="New password"
                value={pw1}
                onChange={(e) => setPw1(e.target.value)}
                required
                autoFocus
                className="h-10"
              />
              <PasswordStrengthMeter password={pw1} />
              <PasswordInput
                data-testid="reset-password-confirm"
                testIdBase="reset-password-confirm"
                placeholder="Confirm new password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                required
                className="h-10"
              />
              {error && <p className="text-destructive text-xs" data-testid="reset-password-error">{error}</p>}
              <Button
                type="submit"
                disabled={submitting || !validatePassword(pw1).valid || pw1 !== pw2}
                className="w-full h-10 font-semibold"
                data-testid="reset-password-submit"
              >
                {submitting ? 'Updating...' : 'Update password'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
